import React, { Component, Dispatch } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { SofaButton, SofaPanel } from '@sofology/sofologycomponents';

import './OrderSummaryPage.scss';

import { AppState } from 'store';
import { generateDispatchToPropsMap, goToExternalUrl, paymentHelper, userHelper } from 'helpers';
import { OrderSummaryNote } from './';
import { initOrderSummaryPage, InitOrderSummaryPageFunc } from 'store/checkout/actions';
import { PaymentDetailsModel } from 'models/Payment';
import { NotificationTypes, PlacedOrderModel } from 'models';
import { BasketErrorModel } from 'models/Errors';
import { addNotification, AddNotificationFunc } from 'store/notifications/actions';
import { CheckoutEstimate, CheckoutOrderTotal, Loader, OrderSummaryItems } from 'components';
import { routes } from 'routes';
import { basketStorage } from 'storages';

interface OwnProps {
}

interface StateProps {
    paymentDetails: PaymentDetailsModel;
    isOrderSummaryPageInitializing: boolean;
    error: BasketErrorModel | null;
    order: PlacedOrderModel | null;
}

interface DispatchProps {
    initOrderSummaryPage: InitOrderSummaryPageFunc;
    addNotification: AddNotificationFunc;
}

const DELAY_BEFORE_REDIRECT = 3000;

class OrderSummaryPage extends Component<OwnProps & StateProps & DispatchProps & RouteComponentProps> {
    componentDidMount() {
        this.props.initOrderSummaryPage(this.getCurrentLocation());
    }

    componentDidUpdate(prevProps: Readonly<OwnProps & StateProps & DispatchProps & RouteComponentProps>) {
        if (!prevProps.error?.message && this.props.error?.message) {
            this.props.addNotification(this.props.error.message, NotificationTypes.Err);

            setTimeout(() => {
                this.props.history.push(routes.basket.path);
            }, DELAY_BEFORE_REDIRECT);
        }
    }

    handleContinueClick = () => {
        basketStorage.preserveOrder(this.props.order!);
        basketStorage.resetEcommerceStorage();
        goToExternalUrl(routes.external.newIn.path);
    };

    render(): JSX.Element {
        const { order, isOrderSummaryPageInitializing } = this.props;

        return (
            <div className='order-summary-page'>
                {
                    (isOrderSummaryPageInitializing && !order)
                        ? <Loader show={isOrderSummaryPageInitializing} />
                        : order && this.renderContent()
                }
            </div>
        );
    }

    renderContent(): JSX.Element {
        const user = userHelper.getUser();

        const shouldShowOrderSummaryNote = user?.username && user.username.includes('@');

        return (
            <>
                {shouldShowOrderSummaryNote && this.renderOrderSummary()}
                {this.renderSummaryPanel()}
                {this.renderInfoNote()}
                {this.renderContinueButton()}
                {this.renderShowOffText()}
            </>
        );
    }

    renderOrderSummary(): JSX.Element {
        const { order } = this.props;

        const email = order!.user!.name;
        const hasDonation = order!.hasDonationProduct();
        const isFinanceOrder = paymentHelper.isCreditRelatedPaymentType(this.props.paymentDetails.paymentType);

        return (<OrderSummaryNote email={email} hasDonation={hasDonation} isFinanceOrder={isFinanceOrder} />);
    }

    renderSummaryPanel(): JSX.Element {
        const { order, paymentDetails } = this.props;
        const deliverNotBeforeDate = order?.eca ? new Date(order.eca) : null;

        return (
            <SofaPanel>
                <OrderSummaryItems useOrderEntries={true} opened={true} />
                <div className='order-summary-page__panel-info'>
                    <CheckoutEstimate
                        leadTime={order!.leadTime!}
                        deliveryCost={order!.deliveryCost?.value}
                        deliverNotBefore={deliverNotBeforeDate}
                        hasQuickstockItem={order!.hasQuickstockItem()}
                    />
                    <CheckoutOrderTotal
                        paymentDetails={paymentDetails}
                        discounts={order!.totalDiscounts}
                        isDepositPaid={true}
                    />
                </div>
            </SofaPanel>
        );
    }

    renderInfoNote(): JSX.Element {
        return (
            <p className='order-summary-page__info-note'>As part of their PlanTree
                initiative, Sofology Limited have partnered with the Woodland Trust (registered charity number England
                and Wales: No: 294344, Scotland No. SC038885) to fund the planting of trees in the UK. Sofology will
                donate £1.25 in respect of each completed and fulfilled order for Sofology furniture and accessories
                that is made between 16th March 2020 and 16th March 2023 (inclusive) *Sofology care kits are excluded
                from this promotion *.</p>
        );
    }

    renderContinueButton(): JSX.Element {
        return (
            <div className='order-summary-page__continue-button'>
                <SofaButton
                    text='Continue shopping'
                    variant='primary'
                    onClick={this.handleContinueClick}
                />
            </div>
        );
    }

    renderShowOffText(): JSX.Element {
        return (
            <div className='order-summary-page__show-off'>
                <p>Show off your new buy and await the round of applause from your friends and followers</p>
            </div>
        );
    }

    getCurrentLocation(): string {
        return this.props.location.pathname;
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        paymentDetails: state.paymentState.paymentDetails,
        isOrderSummaryPageInitializing: state.checkoutState.isOrderSummaryPageInitializing,
        error: state.checkoutState.summaryPageError,
        order: state.checkoutState.order
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [initOrderSummaryPage, addNotification]);
}

export default connect(mapStateToProps, mapDispatchToProps)(OrderSummaryPage);